import * as am4core from '@amcharts/amcharts4/core'
import { MapPolygonSeries, MapPolygon, MapChart } from '@amcharts/amcharts4/maps'
import * as am4maps from '@amcharts/amcharts4/maps'
import am4SwissGeoData from '@/assets/geodata/switzerland.json'

export enum Region {
  NWS = 'Nordwestschweiz',
  OS = 'Ostschweiz',
  EML = 'Espace Mittelland',
  GS = 'Genferseeregion',
  ZS = 'Zentralschweiz',
  TI = 'Tessin',
  ZH = 'Zurich',
}

export interface MapData {
  CNTRY: string
  TYPE: string
  fill: string
  id: string
  name: string
  region: Region
  value: number
}

export interface RegionData {
  data: MapData
  domID: string | null
}

export interface TooltipLabels {
  name: string
  region: string
}

interface Point {
  x: number
  y: number
}

interface HitEvent {
  event: PointerEvent
  point: Point
  spritePoint: Point
  svgPoint: Point
  target: MapPolygon
  touch: boolean
  type: string
}

export class MapBridge {
  private chart: MapChart
  private polygonSeries: MapPolygonSeries | null = null
  private polygonTemplate: MapPolygon | null = null
  /**
   * Given a configuration objects, initializes the Map
   * so that you can later call `createMap()` to render the Map...
   */
  constructor(
    private hoverColor: string,
    private activeColor: string,
    private mapWrapperID: string,
    private selectedRegionId: string | undefined,
    private polygonClickHandler: (regionData: RegionData) => void,
    private mapLoadedCallBack: () => boolean,
    private tooltipLabelResolver?: (data: MapData) => TooltipLabels
  ) {
    this.chart = am4core.create(document.querySelector(this.mapWrapperID) as HTMLElement, am4maps.MapChart)
    this.chart.mouseWheelBehavior = 'none'
  }

  /**
   * An event handler for the exposed Polygon `hit` Event.
   * Simply highlights the `last selected` or the `clicked` map-polygon when `hit`
   * @param ev
   */
  handlePolygonHit = (ev: HitEvent): void => {
    const target = ev.target as MapPolygon
    const parent = target.parent as MapPolygonSeries

    // GET THE CONTEXT DATA FOR THE CLICKED POLYGON...
    const contextData = ev.target.dataItem.dataContext as MapData & {
      multiPolygon?: [number, number][][][][]
      madeFromGeoData?: boolean
      mfh?: string
    }

    // REMOVE EXTRANEOUS DATA FROM THE DATA CONTEXT
    delete contextData.multiPolygon
    delete contextData.madeFromGeoData
    delete contextData.mfh

    const regionData: RegionData = {
      data: ev.target.dataItem.dataContext as MapData,
      domID: ev.target.dom.getAttribute('id'),
    }

    this.polygonClickHandler(regionData)
    this.updateClickedMapPolygonState(parent, ev.target)
    ev.target.isActive = true
  }

  /**
   * Creates the Map using either default values or the latest `regionData` from Store.
   * This is so that state is not lost when a user navigates away and then back...
   */
  createMap(): void {
    this.chart.geodata = am4SwissGeoData
    this.chart.projection = new am4maps.projections.Miller()
    this.polygonSeries = this.chart.series.push(new MapPolygonSeries())
    this.polygonTemplate = this.polygonSeries.mapPolygons.template
    this.polygonSeries.useGeodata = true
    this.chart.logo.disabled = true
    this.chart.seriesContainer.draggable = false
    this.chart.seriesContainer.resizable = false
    this.chart.maxZoomLevel = 1
    this.setToolTips()
    this.setStatesAndLabel()
    this.polygonTemplate.events.on<HitEvent, any>('hit', this.handlePolygonHit)

    this.polygonSeries.events.on(
      'ready',
      () => {
        this.mapLoadedCallBack()
        if (!this.polygonSeries) return
        this.polygonSeries.mapPolygons.each((mapPolygon) => {
          const dataContext = mapPolygon.dataItem.dataContext as MapData
          mapPolygon.fill = am4core.color(dataContext.fill)
          if (dataContext.fill) {
            mapPolygon.fill = am4core.color(dataContext.fill)
            if (this.selectedRegionId === dataContext.id) {
              mapPolygon.isActive = true
            }
          }
        })
        const wrapper = document.querySelector(this.mapWrapperID)
        if (!wrapper) return
        if (wrapper.classList.contains('has-overlay')) {
          wrapper.classList.remove('has-overlay')
        }
      },
      this
    )

    // SET CANTON ABBREVIATIONS AS LABELS ON TOP OF EACH MAP POLYGON [WHITE TEXT]
    const labelSeries = this.chart.series.push(new am4maps.MapImageSeries())
    const labelTemplate = labelSeries.mapImages.template.createChild(am4core.Label)
    labelTemplate.horizontalCenter = 'middle'
    labelTemplate.verticalCenter = 'middle'
    labelTemplate.fontSize = 10
    labelTemplate.nonScaling = true
    labelTemplate.interactionsEnabled = false
    this.polygonSeries.events.on('inited', () => {
      if (!this.polygonSeries) return
      this.polygonSeries.mapPolygons.each(function (polygon) {
        const label = labelSeries.mapImages.create()
        const state = (polygon.dataItem.dataContext as MapData).id.split('_').pop()
        label.latitude = polygon.visualLatitude
        label.longitude = polygon.visualLongitude
        const firstChild = label.children.getIndex(0)
        if (!firstChild) return
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        firstChild.text = state
        firstChild.fill = am4core.color('white')
      })
    })
  }

  /**
   * Updates the Clicked Map-Polygon Status as in: `ACTIVE` or `INACTIVE`when a map-polygon is hit.
   * @param parent
   * @param target
   * @param forceActive
   */
  updateClickedMapPolygonState(parent: MapPolygonSeries, target: MapPolygon, forceActive = false): void {
    const children = parent.children.values
    if (this.selectedRegionId !== null) {
      for (const child of children) {
        if (child === target) {
          if (forceActive) {
            child.isActive = true
          } else {
            child.isActive = !child.isActive
          }
        } else {
          child.isActive = false
        }
      }
    }
  }

  /**
   * Creates hover and active states for the Map-Polygons.
   * Optionally (if uncommented) sets the Map-Label
   * @returns {MapBridge}
   */
  setStatesAndLabel(): MapBridge {
    if (!this.polygonTemplate) return this
    // CREATE NECESSARY STATES AND SET ALTERNATIVE FILL COLOR
    const hoverState = this.polygonTemplate.states.create('hover')
    hoverState.properties.fill = this.hoverColor as unknown as am4core.Color

    // CREATE ACTIVE STATE
    const activeState = this.polygonTemplate.states.create('active')
    activeState.properties.fill = am4core.color(this.activeColor)
    activeState.properties.stroke = am4core.color('#FFFFFF')
    return this
  }

  /**
   * Sets the Map-Tooltips and configures it....
   * Here one could set the fontSize, Color, etc of the Tooltip to customize it further...
   * @returns {MapBridge}
   */
  setToolTips(): MapBridge {
    if (!this.polygonSeries) return this
    this.polygonTemplate = this.polygonSeries.mapPolygons.template
    this.polygonTemplate.tooltipText = '[bold]{name}[/]\n{region}'
    if (this.tooltipLabelResolver) {
      ;(this.polygonTemplate as any).adapter.add('tooltipText', (_tooltipText: string, target: MapPolygon) => {
        const context = target?.dataItem?.dataContext as MapData | undefined
        if (!context) return '[bold]{name}[/]\n{region}'
        const localizedLabels = this.tooltipLabelResolver?.(context)
        if (!localizedLabels) return '[bold]{name}[/]\n{region}'
        return `[bold]${localizedLabels.name}[/]\n${localizedLabels.region}`
      })
    }
    this.polygonTemplate.fill = am4core.color(this.activeColor)
    if (this.polygonSeries.tooltip) {
      const tooltip = this.polygonSeries.tooltip
      tooltip.fontSize = '0.8rem'
      tooltip.fontFamily = 'DINPro'
      tooltip.pointerOrientation = 'vertical'
      tooltip.getFillFromObject = false
      tooltip.filters.clear()
      tooltip.background.filters.clear()
      tooltip.padding(0, 0, 0, 0)
      tooltip.background.fill = am4core.color('#000000')
      tooltip.background.fillOpacity = 1
      tooltip.background.strokeOpacity = 0
      tooltip.background.strokeWidth = 0
      tooltip.background.cornerRadius = 6
      tooltip.label.fill = am4core.color('#FFFFFF')
      tooltip.label.padding(8, 8, 8, 8)
      tooltip.label.textAlign = 'middle'
      tooltip.label.textValign = 'middle'
    }
    this.polygonTemplate.strokeWidth = 1
    this.polygonTemplate.stroke = am4core.color('#D4D4D4')
    return this
  }

  dispose() {
    this.chart.dispose()
  }
}
